// render-video.ts — builds one video card's DOM (<video> + management buttons), reproducing
// display_item_videos() / generate_video_buttons(): set-primary + soft-delete, gated on
// can_manage. Same createElement/textContent escaping as the picture renderer.

import type { ItemMediaConfig, ItemVideo } from './types';
import { runAction } from './actions';

export interface VideoCardCtx {
    config: ItemMediaConfig;
    itemId: string;
    canManage: boolean;
    scope: HTMLElement; // the videos grid, for scoping the "demote all other primaries" sweep
    onRemove?: () => void; // called after a card is hidden (delete) so the count badge stays in sync
}

export function renderVideoCard(video: ItemVideo, ctx: VideoCardCtx): HTMLElement {
    const { config } = ctx;

    const col = document.createElement('div');
    col.className = 'col-md-3 p-2';
    col.id = 'div_video_' + video.video_id;

    const playerWrap = document.createElement('div');
    playerWrap.className = 'd-flex align-items-center justify-content-center';
    const videoEl = document.createElement('video');
    videoEl.className = 'img-thumbnail';
    videoEl.muted = true;
    videoEl.controls = true;
    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('preload', 'metadata');
    if (video.url) {
        const source = document.createElement('source');
        source.src = video.url;
        source.type = video.mime || 'video/mp4';
        videoEl.appendChild(source);
    }
    playerWrap.appendChild(videoEl);
    col.appendChild(playerWrap);

    const body = document.createElement('div');

    if (video.name) {
        const nameEl = document.createElement('div');
        nameEl.className = 'mb-2';
        nameEl.textContent = video.name;
        body.appendChild(nameEl);
    }

    if (ctx.canManage) {
        const bar = document.createElement('div');
        bar.className = 'my-3 d-flex justify-content-center align-items-center flex-wrap gap-2';

        const primaryBtn = document.createElement('button');
        primaryBtn.type = 'button';
        primaryBtn.className = 'btn btn-sm ft-media-video-primary ' + (video.primary ? 'btn-info' : 'btn-primary');
        primaryBtn.innerHTML = config.icons.primary;
        primaryBtn.addEventListener('click', () => {
            void runAction(config.videoPrimaryEndpoint, { video_id: video.video_id }, config.statusDivId);
            ctx.scope.querySelectorAll<HTMLElement>('.ft-media-video-primary').forEach((b) => {
                b.classList.remove('btn-info');
                b.classList.add('btn-primary');
            });
            primaryBtn.classList.remove('btn-primary');
            primaryBtn.classList.add('btn-info');
        });
        bar.appendChild(primaryBtn);

        const delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'btn btn-danger btn-sm';
        delBtn.innerHTML = config.icons.delete;
        delBtn.addEventListener('click', () => {
            void runAction(
                config.videoDeleteEndpoint,
                { item_type: 'video', video_id: video.video_id, attached_id: ctx.itemId },
                config.statusDivId,
            );
            col.style.display = 'none';
            ctx.onRemove?.();
        });
        bar.appendChild(delBtn);

        body.appendChild(bar);
    }

    if (video.description) {
        const desc = document.createElement('div');
        desc.className = 'border rounded my-2 p-1';
        desc.textContent = video.description;
        body.appendChild(desc);
    }

    col.appendChild(body);
    return col;
}
